/**
 * @playanext/playa-yield-sdk - Chrome Extension Ad Monetization SDK
 * Manifest V3 compliant ad SDK for Chrome extensions
 */

import type {
  AdOptions,
  AdResult,
  ManagedRefreshableAdOptions,
  ManagedRefreshableAdResult,
  AdCreative,
} from './types';
import { ALLOWED_AD_SIZES, ConfigError, MessageType } from './types';
import { getConfig, requestAd } from './request';
import { createAdElement, updateAdElement } from './ad-renderer';
import { sendToBackground } from './messaging';
import { generateUUID, validatePlacementContext } from './utils';
import { logger } from './logger';

const managedAdCleanupByContainer = new WeakMap<HTMLElement, () => void>();

// Re-export types and initialization function for consumers
export type {
  SDKConfig,
  AdOptions,
  AdResult,
  Size,
  Placement,
  ManagedRefreshableAdOptions,
  ManagedRefreshableAdResult,
} from './types';
export { ALLOWED_AD_SIZES } from './types';
export { MessageType } from './types';
export { initializePlayaYield } from './background-handler';

function isAllowedAdSize(width: number, height: number): boolean {
  return ALLOWED_AD_SIZES.some(
    (size) => size.width === width && size.height === height
  );
}

function validateAdSize(options: AdOptions): void {
  if (!options.size || !options.size.width || !options.size.height) {
    throw new ConfigError('size with width and height is required');
  }

  if (!isAllowedAdSize(options.size.width, options.size.height)) {
    throw new ConfigError(
      'Unsupported ad size. Allowed sizes are 300x250, 320x50, and 320x100.'
    );
  }
}

/**
 * Request and create an ad element
 * Returns a ready-to-append div element containing the ad HTML
 *
 * No initialization needed in content scripts - just call this function!
 * Make sure initializePlayaYield() is called in your background script.
 *
 * @param options Ad request options
 * @returns Promise resolving to AdResult with div element and metadata
 * @throws {ConfigError} If SDK not initialized in background
 * @throws {NetworkError} If ad request fails
 *
 * @example
 * ```ts
 * import { createAd } from '@playanext/playa-yield-sdk';
 *
 * const { element, impressionId } = await createAd({
 *   placement: 'internal',
 *   size: { width: 300, height: 250 },
 *   categories: ['tech', 'gaming']
 * });
 *
 * document.getElementById('ad-slot')!.appendChild(element);
 * ```
 */
export async function createAd(options: AdOptions): Promise<AdResult> {
  // Validate options first
  if (!options.placement) {
    throw new ConfigError('placement is required');
  }

  validateAdSize(options);

  // Validate execution context (placement must match where the code is running)
  validatePlacementContext(options.placement);

  // Get config from background (verifies SDK is initialized)
  await getConfig();

  // Request ad from backend (backend uses placement to authorize the request)
  const creative = await requestAd(options);

  // Create and return ad element
  const result = createAdElement(creative);

  logger.log('Ad displayed', {
    placement: options.placement,
    impressionId: result.impressionId,
  });

  return result;
}

/**
 * Create a fully managed refreshable ad
 *
 * The SDK handles:
 * - Appending the ad to your container
 * - Auto-cleanup when the container is removed from DOM
 * - All refresh logic internally
 *
 * @param options Configuration including container element or CSS selector
 * @returns Simple control object with stop() method
 *
 * @example
 * ```ts
 * // Simplest usage - just provide container
 * await createManagedRefreshableAd({
 *   container: '#ad-slot',
 *   placement: 'sidepanel',
 *   size: { width: 300, height: 250 }
 * });
 *
 * // Or with element reference
 * await createManagedRefreshableAd({
 *   container: document.getElementById('ad-slot'),
 *   placement: 'sidepanel',
 *   size: { width: 300, height: 250 }
 * });
 * ```
 */
export async function createManagedRefreshableAd(
  options: ManagedRefreshableAdOptions
): Promise<ManagedRefreshableAdResult> {
  // Resolve container
  const resolvedContainer =
    typeof options.container === 'string'
      ? document.querySelector(options.container)
      : options.container;

  if (!(resolvedContainer instanceof HTMLElement)) {
    throw new ConfigError(
      `Container not found: ${
        typeof options.container === 'string'
          ? options.container
          : 'provided element'
      }`
    );
  }

  const container = resolvedContainer;

  // Create the refreshable ad (internal function)
  // Remove container from options to avoid serialization issues when sending to background
  const { container: _, ...adOptions } = options;
  const refreshable = await createRefreshableAdInternal(adOptions);

  const existingCleanup = managedAdCleanupByContainer.get(container);
  if (existingCleanup) {
    existingCleanup();
  }

  Array.from(container.children).forEach((child) => {
    if (
      child instanceof HTMLElement &&
      child.hasAttribute('data-playayield-id')
    ) {
      child.remove();
    }
  });

  // Append to container
  container.appendChild(refreshable.element);

  logger.log('Managed ad appended to container', {
    placement: options.placement,
    container:
      typeof options.container === 'string'
        ? options.container
        : options.container.id || 'element',
  });

  // Set up MutationObserver to detect when element is removed from DOM
  let observer: MutationObserver | null = null;

  const checkIfRemoved = () => {
    if (!refreshable.element.isConnected) {
      logger.log('Ad removed from DOM, auto-stopping refresh');
      refreshable.stop();
      observer?.disconnect();
    }
  };

  observer = new MutationObserver(checkIfRemoved);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  // Also check periodically as a fallback
  const checkInterval = setInterval(checkIfRemoved, 1000);

  const stopManagedAd = () => {
    refreshable.stop();
    observer?.disconnect();
    clearInterval(checkInterval);

    if (managedAdCleanupByContainer.get(container) === stopManagedAd) {
      managedAdCleanupByContainer.delete(container);
    }
  };

  managedAdCleanupByContainer.set(container, stopManagedAd);

  // Return simple control interface
  return {
    stop: stopManagedAd,
    isActive: () => refreshable.isActive(),
  };
}

/**
 * Internal version of refreshable ad - not exposed to developers
 * SDK handles all refresh logic internally without callbacks
 * Refresh config is dynamically controlled by backend via AdCreative
 */
async function createRefreshableAdInternal(options: AdOptions): Promise<{
  element: HTMLDivElement;
  ad: AdCreative;
  stop: () => void;
  isActive: () => boolean;
}> {
  // Validate options first
  if (!options.placement) {
    throw new ConfigError('placement is required');
  }

  validateAdSize(options);

  // Validate execution context (placement must match where the code is running)
  validatePlacementContext(options.placement);

  // Get config from background (verifies SDK is initialized)
  await getConfig();

  // Default constants (fallbacks if backend doesn't provide config)
  const DEFAULT_REFRESH_INTERVAL_MS = 40000;
  const DEFAULT_MAX_IMPRESSIONS_PER_SESSION = 29;

  // Generate unique ID for this refreshable ad instance
  const adInstanceId = generateUUID();

  logger.log('Fetching initial ad...', {
    placement: options.placement,
    adInstanceId,
  });

  // Request first ad without config (we'll get it from the ad response)
  const initialCreative = await requestAd(options);

  // Extract refresh config from backend response, use defaults as fallback
  const refreshConfig = initialCreative.refreshConfig;
  const refreshIntervalMs =
    refreshConfig?.intervalMs ?? DEFAULT_REFRESH_INTERVAL_MS;
  const maxImpressionsPerSession =
    refreshConfig?.maxImpressionsPerSession ??
    DEFAULT_MAX_IMPRESSIONS_PER_SESSION;
  const sessionWindowMs = refreshConfig?.sessionWindowMs ?? 30 * 60 * 1000;

  logger.log('Refresh config received from backend', {
    refreshIntervalMs,
    maxImpressionsPerSession,
    sessionWindowMs,
    backendProvided: !!refreshConfig,
  });

  // Check session limits with backend-provided max and window
  const sessionCheck = await sendToBackground<{
    allowed: boolean;
    remaining: number;
    total: number;
  }>({
    type: MessageType.UPDATE_SESSION_IMPRESSION,
    payload: {
      adId: adInstanceId,
      maxImpressions: maxImpressionsPerSession,
      sessionWindowMs,
    },
  });

  logger.log('Session check result', {
    allowed: sessionCheck.data?.allowed,
    totalImpressions: sessionCheck.data?.total,
    remaining: sessionCheck.data?.remaining,
  });

  if (!sessionCheck.data?.allowed) {
    logger.log('Initial ad blocked: max impressions reached for session');
    throw new ConfigError(
      'Cannot display ad: max impressions reached for this session'
    );
  }

  logger.log('Initial ad fetched successfully', {
    impressionId: initialCreative.impressionId,
    provider: initialCreative.advertiser || 'unknown',
  });

  // Create the ad element
  const result = createAdElement(initialCreative);
  let currentCreative = initialCreative;
  let isActive = true;

  // Visibility tracking for smart refresh
  let isTabVisible = !document.hidden;
  let isAdInViewport = false;
  let visibilityObserver: IntersectionObserver | null = null;

  // Page Visibility API - track when tab is hidden/minimized
  const handleVisibilityChange = () => {
    isTabVisible = !document.hidden;
    logger.log('Tab visibility changed:', { isTabVisible });
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // IntersectionObserver - track when ad is actually in viewport
  if (typeof IntersectionObserver !== 'undefined') {
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        isAdInViewport = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        logger.log('Ad viewport visibility:', {
          isAdInViewport,
          ratio: entry.intersectionRatio,
        });
      },
      { threshold: [0, 0.5, 1] }
    );
    visibilityObserver.observe(result.element);
  } else {
    // Fallback: assume visible if observer not available
    isAdInViewport = true;
  }

  // Set up the refresh interval using backend-provided config
  logger.log('Starting refresh timer', {
    refreshIntervalMs,
    nextRefreshIn: `${refreshIntervalMs / 1000}s`,
    source: refreshConfig ? 'backend' : 'default',
  });

  const timerId = setInterval(async () => {
    if (!isActive) return;

    // Only refresh if tab is visible AND ad is in viewport
    if (!isTabVisible || !isAdInViewport) {
      logger.log('Refresh skipped: not visible', {
        isTabVisible,
        isAdInViewport,
      });
      return;
    }

    logger.log('Refresh triggered (visible), fetching new ad...');

    try {
      // Request new ad and check session limits with backend-provided config
      const newCreative = await requestAd(options);

      // Extract refresh config from new ad response
      const newRefreshConfig = newCreative.refreshConfig;
      const newMaxImpressions =
        newRefreshConfig?.maxImpressionsPerSession ?? maxImpressionsPerSession;
      const newSessionWindowMs =
        newRefreshConfig?.sessionWindowMs ?? sessionWindowMs;

      // Check session limits with possibly updated config
      const newSessionCheck = await sendToBackground<{
        allowed: boolean;
        remaining: number;
        total: number;
      }>({
        type: MessageType.UPDATE_SESSION_IMPRESSION,
        payload: {
          adId: adInstanceId,
          maxImpressions: newMaxImpressions,
          sessionWindowMs: newSessionWindowMs,
        },
      });

      if (!newSessionCheck.data?.allowed) {
        logger.log(
          'Refresh blocked: max impressions reached (will retry on next interval)'
        );
        return;
      }

      logger.log('New ad fetched for refresh', {
        impressionId: newCreative.impressionId,
        provider: newCreative.advertiser || 'unknown',
      });

      // Update the existing ad element with new creative
      updateAdElement(result.element, newCreative);
      currentCreative = newCreative;

      logger.log('Ad refreshed successfully', {
        placement: options.placement,
        impressionId: newCreative.impressionId,
      });
    } catch (error) {
      logger.error('Failed to refresh ad:', error);
      // Continue trying on next interval - don't stop on transient errors
    }
  }, refreshIntervalMs);

  logger.log('Refreshable ad created', {
    adInstanceId,
    placement: options.placement,
    refreshInterval: refreshIntervalMs,
    maxImpressions: maxImpressionsPerSession,
    refreshSource: refreshConfig ? 'backend' : 'default',
  });

  return {
    element: result.element,
    ad: currentCreative,
    stop: () => {
      if (!isActive) return;
      isActive = false;
      clearInterval(timerId);

      // Clean up visibility tracking
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      visibilityObserver?.disconnect();

      // Notify background to cleanup session tracking
      sendToBackground({
        type: MessageType.STOP_REFRESHABLE_AD,
        payload: { adId: adInstanceId },
      }).catch(() => {}); // Ignore errors on cleanup

      logger.log('Refreshable ad stopped', { adInstanceId });
    },
    isActive: () => isActive,
  };
}
